/**
 * The shapes the Files picker (F-DMS) speaks.
 *
 * Every pick resolves to Files - a document picked from Google Drive or
 * OneDrive / SharePoint is imported into Files by the backend first, so a
 * consumer always receives Files uuids, whatever the source.
 */

export type PickedFileSource = 'files' | 'gdrive' | 'onedrive';

export interface PickedFile {
  /** The Files uuid (`FileID`). */
  uuid: string;
  name: string;
  size?: number;
  mime_type?: string;
  source: PickedFileSource;
  /** Folder the file lives in, when known (`''` = root). Needed to stream its bytes. */
  folder_uuid?: string;
}

export interface FilesPickerOptions {
  /** Allowed extensions without dots (`['pdf', 'docx']`). Others are shown but cannot be picked. */
  accept?: string[];
  /** Default true. When false, picking a file replaces the selection. */
  multiple?: boolean;
  /** Upper bound on how many files can be selected. */
  maxFiles?: number;
  /** Replaces the default "Select from Files" title. */
  title?: string;
  /** Which tabs to offer. Default: all (external ones only when the provider exists). */
  sources?: PickedFileSource[];
  /** Tab to open on. Default `'files'`. */
  initialSource?: PickedFileSource;
  /** Files folder that external imports land in. Backend default when omitted. */
  importFolderUuid?: string;
}

export type ExternalProvider = Exclude<PickedFileSource, 'files'>;

/** What a provider picker hands back: provider item ids, never bytes. */
export interface ExternalPickedItem {
  id: string;
  /** OneDrive / SharePoint drive the item lives in (`parentReference.driveId`). */
  drive_id?: string;
  name?: string;
}

/** Where the integrations (connect / disconnect) live in Settings. */
export const INTEGRATIONS_SETTINGS_PATH = '/settings/integrations';

/** Lower-cased extension of a file name, without the dot (`''` when none). */
export const fileExtension = (name: string): string => {
  const match = /\.([^./\\]+)$/.exec(name || '');
  return match ? match[1].toLowerCase() : '';
};

/** Whether a name passes an `accept` list. An empty/absent list accepts everything. */
export const isAccepted = (name: string, accept?: string[]): boolean => {
  if (!accept || accept.length === 0) return true;
  const ext = fileExtension(name);
  return accept.some((a) => a.replace(/^\./, '').toLowerCase() === ext);
};
